
import { useReducer } from 'react';
import { useCentralApi, useCurrentUser } from 'hooks/shared';
import { IWorkItemDraft } from 'models/work-items';
import useWorkItemDraftContext from './useWorkItemDraftContext';
import { TaskStatus } from 'models/tasks';

type FieldUpdater = (newValue: any) => Promise<boolean>;
type WorkingChanged = (working: boolean) => void;
type PostUpdateSignature = (data: IWorkItemDraft) => void;

type WorkItemDraftCreateEvents = {
    onCreate?: PostUpdateSignature;
    onWorkingChanged?: WorkingChanged;
};

type useWorkItemDraftCreateResult = [
    FieldUpdater,
    boolean
];

type useWorkItemDraftCreateSignature = (
    events?: WorkItemDraftCreateEvents
) => useWorkItemDraftCreateResult;

const useWorkItemDraftCreate: useWorkItemDraftCreateSignature = (events) => {
    const { workItem, updateWorkItemDraft } = useWorkItemDraftContext();
    const { onCreate, onWorkingChanged } = events ?? {};

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const [ working, setWorking ] = useReducer((state: boolean, value: boolean) => {
        if (state !== value) {
            onWorkingChanged?.(value);
        }
        return value;
    }, false);
    
    const fieldUpdater = async (newValue: string) => {
        if (!user || !user.access_token) 
            return false;
        if (workItem !== null)
            return false;
        if (!newValue)
            return false;
        if (newValue.trim().length === 0)
            return false;

        try {
            setWorking(true);
            const req:IWorkItemDraft = {
                id: 0,
                name: newValue,
                statusId: TaskStatus.InProgress
            };
            
            const resp = await fetcher(`/api/v3/drafts/workitems`, {
                method: 'POST',
                body: JSON.stringify(req)
            });

            if (!resp.ok) {
                if (resp.status === 400) {
                    return false;
                }
            }

            const result = await resp.json() as IWorkItemDraft;
            updateWorkItemDraft(result);
            onCreate?.(result);
            return true;
        } catch (err) {
            return false;
        } finally {
            setWorking(false);
        }
    };

    return [
        fieldUpdater,
        working
    ];
};

export default useWorkItemDraftCreate;